import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Col, Form, Input, Row } from 'antd';
import {
  StyledButton,
  StyledColumn,
  StyledFormItem,
  StyledLabel,
  StyledTableContainer
} from './styled';
import { CustomHeaderV2 } from '../../components/CustomHeaderComponent/customHeader';
import { TitleComponent } from '../../components/TableComponents';
import { StyledTable } from '../styled';
import { concat, pullAll } from 'lodash';
import { challengerNameRules, typologyDeselectRule } from './FormRules';

const TypologyConfig = ({
  isScenarioLoading,
  typologies,
  championTypologies,
  onCreate
}) => {
  const [form] = Form.useForm();
  const [hasFormError, setHasFormError] = useState(true);
  const [selectedTypologies, setSelectedTypologies] = useState({});
  const [selectedChampionTypologies, setSelectedChampionTypologies] = useState(
    {}
  );
  const [allTypologyIds, setAllTypologyIds] = useState([]);
  const [disabledCombinations, setDisabledCombinations] = useState([]);

  // Typologies selected in default, so prepare all ids.
  useEffect(() => {
    const allTypologies = {};

    typologies.map(
      ({ combinationId, typologies: combinationTypologies }) =>
        (allTypologies[combinationId] =
          combinationTypologies.map(({ id }) => id) || [])
    );

    // Keep the all typology ids in state for validate "reason input" show/hide
    setAllTypologyIds(allTypologies);
    setSelectedTypologies(allTypologies);
  }, [typologies]);

  // Typologies selected in default, so prepare all ids.
  useEffect(() => {
    const allChampionTypologies = {};
    const disabledCombinationIds = [];

    championTypologies.map(
      ({
        combinationId,
        combinationName,
        typologies: combinationTypologies
      }) => {
        const isAvailable = typologies.some(
          ({ combinationName: name }) =>
            name?.toLowerCase() === combinationName?.toLowerCase()
        );

        if (isAvailable) {
          allChampionTypologies[combinationId] =
            combinationTypologies.map(({ id }) => id) || [];
        } else {
          disabledCombinationIds.push(combinationId);
        }
      }
    );

    setDisabledCombinations(disabledCombinationIds);
    setSelectedChampionTypologies(allChampionTypologies);
  }, [championTypologies, typologies]);

  const getColumns = useCallback(
    (index, combinationName, combinationId) => {
      return [
        {
          title: (
            <CustomHeaderV2
              title={`Set - ${combinationName || ''} (${
                selectedTypologies[combinationId]?.length || 0
              } selected)`}
            />
          ),
          key: 'typology',
          render: ({ name }) => (
            <StyledColumn>
              <TitleComponent title={name} />
            </StyledColumn>
          )
        }
      ];
    },
    [selectedTypologies]
  );

  const getChampioncolumns = useCallback(
    (index, combinationName, combinationId) => {
      return [
        {
          title: (
            <CustomHeaderV2
              title={`Set - ${combinationName || ''} (${
                selectedChampionTypologies[combinationId]?.length || 0
              } selected)`}
            />
          ),
          key: 'typology',
          render: ({ name }) => (
            <StyledColumn>
              <TitleComponent title={name} />
            </StyledColumn>
          )
        }
      ];
    },
    [selectedChampionTypologies]
  );

  const getRowSelection = useCallback(
    (combinationId) => ({
      type: 'checkbox',
      selectedRowKeys: selectedTypologies[combinationId] || [],
      onChange: (ids) =>
        setSelectedTypologies((values) => ({
          ...values,
          [combinationId]: ids
        })),
      preserveSelectedRowKeys: true,
      columnWidth: 48
    }),
    [selectedTypologies]
  );

  const getChampionRowSelection = useCallback(
    (combinationId) => {
      const isDisable = disabledCombinations.includes(combinationId);

      return {
        type: 'checkbox',
        selectedRowKeys: selectedChampionTypologies[combinationId] || [],
        onChange: (ids) =>
          setSelectedChampionTypologies((values) => ({
            ...values,
            [combinationId]: ids
          })),
        preserveSelectedRowKeys: true,
        columnWidth: 48,
        hideSelectAll: isDisable,
        getCheckboxProps: () => ({
          disabled: isDisable
        })
      };
    },
    [selectedChampionTypologies, disabledCombinations]
  );

  const onCreateClick = () => {
    form
      .validateFields()
      .then((values) => {
        let selected = [];
        let unSelected = [];

        typologies.map(
          ({ combinationId, typologies: combinationTypologies }) => {
            const typologyIds = combinationTypologies.map(({ id }) => id) || [];
            const selectedIds = selectedTypologies[combinationId] || [];

            selected.push({
              segmentCombinationId: combinationId,
              typologyIds: selectedIds
            });
            unSelected.push({
              segmentCombinationId: combinationId,
              typologyIds: pullAll(typologyIds, selectedIds)
            });
          }
        );

        championTypologies.map(
          ({ combinationId, typologies: combinationTypologies }) => {
            const typologyIds = combinationTypologies.map(({ id }) => id) || [];
            const selectedIds = selectedChampionTypologies[combinationId] || [];

            selected.push({
              segmentCombinationId: combinationId,
              typologyIds: selectedIds
            });
            unSelected.push({
              segmentCombinationId: combinationId,
              typologyIds: pullAll(typologyIds, selectedIds)
            });
          }
        );

        // Remove empty typology sets
        selected = selected.filter(({ typologyIds }) => typologyIds.length > 0);
        unSelected = unSelected.filter(
          ({ typologyIds }) => typologyIds.length > 0
        );

        onCreate({
          ...values,
          selectedTypologyIds: selected,
          unselectedTypologyIds: unSelected
        });
      })
      .catch((info) => {});
  };

  const onFormValuesChange = () => {
    setHasFormError(
      !form.isFieldsTouched(true) ||
        form.getFieldsError().some((item) => item.errors.length > 0)
    );
  };

  useEffect(() => {
    onFormValuesChange();
  }, [selectedTypologies]);

  const isDisableButton = useMemo(() => {
    return (
      hasFormError ||
      concat([], ...Object.values(selectedTypologies)).length === 0
    );
  }, [selectedTypologies, hasFormError]);

  const showReasonInput = useMemo(() => {
    const allLength = concat([], ...Object.values(allTypologyIds)).length;
    const selectedLength = concat(
      [],
      ...Object.values(selectedTypologies)
    ).length;

    if (allLength === selectedLength) {
      form.setFieldsValue({ reasonMessage: '' });
      return false;
    } else {
      return true;
    }
  }, [selectedTypologies, allTypologyIds]);

  return (
    <Form form={form} layout="vertical" onFieldsChange={onFormValuesChange}>
      <StyledFormItem
        label="Challenger Name"
        name="challengerName"
        rules={challengerNameRules}>
        <Input maxLength={255} placeholder="Please input the challenger name" />
      </StyledFormItem>
      {!isScenarioLoading && (
        <StyledFormItem label="Please select the typologies shown below">
          <Row gutter={32}>
            <Col span={championTypologies.length > 0 ? 12 : 24}>
              <StyledLabel>Run Instance Typologies</StyledLabel>
              {typologies.map(
                (
                  {
                    combinationId,
                    combinationName,
                    typologies: combinationTypologies
                  },
                  index
                ) => (
                  <StyledTableContainer key={combinationId} index={index}>
                    <StyledTable
                      rowKey={({ id }) => id}
                      columns={getColumns(
                        index,
                        combinationName,
                        combinationId
                      )}
                      dataSource={combinationTypologies || []}
                      rowSelection={getRowSelection(combinationId)}
                      pagination={false}
                    />
                  </StyledTableContainer>
                )
              )}
              {typologies.length === 0 && (
                <StyledTable
                  rowKey={({ id }) => id}
                  columns={getColumns(0, '')}
                  dataSource={[]}
                  rowSelection={getRowSelection(0)}
                  pagination={false}
                />
              )}
            </Col>
            {championTypologies.length > 0 && (
              <Col span={12}>
                <StyledLabel>Current Champion Typologies</StyledLabel>
                {championTypologies.map(
                  (
                    {
                      combinationId,
                      combinationName,
                      typologies: combinationTypologies
                    },
                    index
                  ) => (
                    <StyledTableContainer key={combinationId} index={index}>
                      <StyledTable
                        rowKey={({ id }) => id}
                        columns={getChampioncolumns(
                          index,
                          combinationName,
                          combinationId
                        )}
                        dataSource={combinationTypologies || []}
                        rowSelection={getChampionRowSelection(combinationId)}
                        pagination={false}
                      />
                    </StyledTableContainer>
                  )
                )}
              </Col>
            )}
          </Row>
        </StyledFormItem>
      )}
      {showReasonInput && (
        <StyledFormItem
          label="Please state your reasons for not selecting the typology(ies)"
          name="reasonMessage"
          rules={typologyDeselectRule}>
          <Input maxLength={255} placeholder="Please enter your notes here" />
        </StyledFormItem>
      )}
      <StyledButton
        type="primary"
        onClick={onCreateClick}
        disabled={isDisableButton}>
        Create Challenger
      </StyledButton>
    </Form>
  );
};

TypologyConfig.propTypes = {
  isScenarioLoading: PropTypes.bool,
  typologies: PropTypes.array,
  championTypologies: PropTypes.array,
  onCreate: PropTypes.func
};

export default TypologyConfig;
